import { getPropertyOf, hasPropertyOf } from '@cs/core/utils';

/**
	* Defines a condition that evaluates a column's value
	*/
export class TableRowRuleCondition {
	/**
		* The column id to evaluate
		*/
	column: string;

	/**
		* Operator for comparison using ColdFusion-style strings:
		* - 'eq': equals
		* - 'neq': not equals
		* - 'gt': greater than
		* - 'lt': less than
		* - 'gte': greater than or equal
		* - 'lte': less than or equal
		* - 'contains': string contains
		* - 'isEmpty': value is null/undefined/empty string
		* - 'isNotEmpty': value is not null/undefined/empty string
		*/
	operator: 'eq' | 'neq' | 'gt' | 'lt' | 'gte' | 'lte' | 'contains' | 'isEmpty' | 'isNotEmpty';

	/**
		* The value to compare against (not required for isEmpty/isNotEmpty)
		*/
	value?: any;

	constructor(init: Partial<TableRowRuleCondition>) {
		this.column   = getPropertyOf(init, 'column', null);
		this.operator = getPropertyOf(init, 'operator', 'eq');
		this.value    = getPropertyOf(init, 'value', null);
	}
}

/**
	* Defines an action to apply when a rule matches
	*/
export class TableRowRuleAction {
	/**
		* Type of action:
		* - 'hideContent': Hides the cell content
		* - 'addClass': Adds a CSS class to the cell
		* - 'setStyle': Sets inline styles on the cell
		*/
	type: 'hideContent' | 'addClass' | 'setStyle' | 'appendContent' | 'prependContent' | 'disableClick' | 'enableClick';

	/**
		* The value for the action:
		* - For 'addClass': the class name(s) as a string
		* - For 'setStyle': an object with CSS properties (e.g., { backgroundColor: 'pink' })
		* - For 'hideContent': not used
		*/
	value?: string | {
		[key: string]: string
	};

	constructor(init: Partial<TableRowRuleAction>) {
		this.type  = getPropertyOf(init, 'type', 'addClass');
		this.value = getPropertyOf(init, 'value', null);
	}
}

/**
	* Defines a rule that evaluates conditions based on row data and applies actions to cells
	*/
export class TableRowRule {
	/**
		* Array of conditions to evaluate against row data
		*/
	conditions: TableRowRuleCondition[];

	/**
		* How to combine multiple conditions: 'AND' (all must match) or 'OR' (any must match)
		* Defaults to 'AND'
		*/
	conditionOperator: 'AND' | 'OR';

	/**
		* The column to apply the action to.
		* If not specified, the action applies to the entire row (all cells)
		*/
	targetColumn?: string;

	/**
		* The action to apply when conditions are met
		*/
	action: TableRowRuleAction;

	constructor(init: Partial<TableRowRule>) {
		this.conditions        = hasPropertyOf(init, 'conditions')
																											? init.conditions.map(c => new TableRowRuleCondition(c))
																											: [];
		this.conditionOperator = getPropertyOf(init, 'conditionOperator', 'AND');
		this.targetColumn      = getPropertyOf(init, 'targetColumn', null);
		this.action            = hasPropertyOf(init, 'action')
																											? new TableRowRuleAction(init.action)
																											: new TableRowRuleAction({});
	}

	/**
		* Evaluates the rule conditions against a row's data
		* @param rowData Object containing column values keyed by column id
		* @returns true if the rule conditions are met
		*/
	evaluate(rowData: {
		[key: string]: any
	}): boolean {
		if (this.conditions.length === 0) {
			return false;
		}

		const results = this.conditions.map(condition => this.evaluateCondition(condition, rowData));

		if (this.conditionOperator === 'OR') {
			return results.some(r => r);
		}
		return results.every(r => r);
	}

	private evaluateCondition(condition: TableRowRuleCondition, rowData: {
		[key: string]: any
	}): boolean {
		const cellValue = rowData[condition.column];

		switch (condition.operator) {
			case 'eq':
				return cellValue === condition.value;
			case 'neq':
				return cellValue !== condition.value;
			case 'gt':
				return cellValue > condition.value;
			case 'lt':
				return cellValue < condition.value;
			case 'gte':
				return cellValue >= condition.value;
			case 'lte':
				return cellValue <= condition.value;
			case 'contains':
				return String(cellValue)
					.includes(String(condition.value));
			case 'isEmpty':
				return cellValue === null || cellValue === undefined || cellValue === '';
			case 'isNotEmpty':
				return cellValue !== null && cellValue !== undefined && cellValue !== '';
			default:
				return false;
		}
	}
}
